<template>
    <div class="profile-details">
        <form>
            <input
                v-if="isDataLoaded"
                type="hidden"
                name="id"
                :value="patientId"
            />
        </form>

        <b-row class="skeleton" v-if="!isDataLoaded">
            <b-col md="2" class="image">
                <div></div>
            </b-col>
            <b-col md="5" class="content">
                <div></div>
                <div></div>
                <div></div>
                <div class="smaller"></div>
                <div class="smaller"></div>
                <div></div>
                <div></div>
                <div class="smaller"></div>
                <div></div>
                <div class="smaller"></div>
            </b-col>
            <b-col md="5" class="content">
                <div></div>
                <div></div>
                <div class="smaller"></div>
                <div></div>
                <div class="smaller"></div>
                <div></div>
                <div></div>
                <div></div>
                <div class="smaller"></div>
            </b-col>
        </b-row>

        <b-row v-if="isDataLoaded" class="patient">
            <b-col xl="2" md="2" sm="6">
                <patient-photo :patient="patient" :allowRotate="false" />
            </b-col>
            <b-col xl="5" md="4" sm="6">
                <!-- view -->
                <b-table-simple borderless>
                    <b-tbody>
                        <b-tr>
                            <b-th>Name:</b-th>
                            <b-td>
                                <span>
                                    <router-link
                                        v-if="USE_PATIENT_MODULE_VUE"
                                        :to="{
                                            name: 'patient_profile',
                                            params: {
                                                id:
                                                    patient.encoded_id ||
                                                    patient.encodedId
                                            }
                                        }"
                                        >{{ patient.name }}
                                    </router-link>
                                    <a
                                        v-else
                                        :href="`${CI_APPS}patient/patient/view/${patient.encoded_id}`"
                                        >{{ patient.name }}
                                    </a>
                                </span>
                                <span v-if="patient.primary_owner.length > 0">
                                    (<client-name
                                        showPopover
                                        :title="
                                            getFullName(
                                                patient.primary_owner[0]
                                            )
                                        "
                                        :client="patient.primary_owner[0]"
                                        :text="
                                            getFullName(
                                                patient.primary_owner[0]
                                            )
                                        "
                                    >
                                        <patient-owner-hover
                                            :owner="patient.primary_owner[0]"
                                        />
                                    </client-name>
                                    <span
                                        v-if="patient.active_owners.length > 1"
                                    >
                                        +
                                        <strong
                                            class="text-primary cursor-pointer"
                                            @click="$emit('load')"
                                            >{{
                                                patient.active_owners.length - 1
                                            }}</strong
                                        ></span
                                    >)
                                </span>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Status:</b-th>
                            <b-td>
                                {{ getStatus(patient) }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Breed:</b-th>
                            <b-td>
                                {{ getEntityBreed(patient) }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Color:</b-th>
                            <b-td>
                                {{ patient.color }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Sex:</b-th>
                            <b-td>
                                {{ getSex() }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Age:</b-th>
                            <b-td>
                                {{ patient.age }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th class="w-25">Birthdate:</b-th>
                            <b-td>
                                <span v-if="patient.birthdate">
                                    {{ _carbonDateDisplay(patient.birthdate) }}
                                </span>
                                <span v-else-if="patient.est_birthdate">
                                    {{
                                        _carbonDateDisplay(
                                            patient.est_birthdate
                                        )
                                    }}
                                    (est.)
                                </span>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Chip:</b-th>
                            <b-td>
                                {{ patient.chip }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Weight:</b-th>
                            <b-td>
                                {{ patient.weight }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th class="text-nowrap">Next Visit:</b-th>
                            <b-td>
                                <router-link
                                    v-if="patient.next_visit"
                                    :to="{
                                        name: 'appointment',
                                        params: {
                                            view: 'resourceTimeGridDay',
                                            start: getDateInSeconds(
                                                patient.next_visit
                                            )
                                        }
                                    }"
                                    >{{
                                        _carbonDateTimeDisplay(
                                            patient.next_visit
                                        )
                                    }}
                                </router-link>
                                <span v-else>Not scheduled</span>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-col>
            <b-col v-if="mode === 'EDIT'" xl="5" md="6" sm="12" class="details">
                <lab-edit
                    ref="labEdit"
                    :lab="lab"
                    :mode="'EDIT'"
                    :isSaving="isSaving"
                    @edit-saved="editSaved"
                />
            </b-col>
            <b-col v-else xl="5" md="6" sm="12">
                <b-table-simple borderless>
                    <b-tbody>
                        <b-tr>
                            <b-th>Source:</b-th>
                            <b-td>{{ getLabSource(lab.data) }} </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Item(s):</b-th>
                            <div
                                v-for="item in lab.data.lab_items"
                                :key="item.id"
                            >
                                <b-td>{{ item.name }}</b-td>
                            </div>
                        </b-tr>
                        <b-tr>
                            <b-th>Ordered:</b-th>
                            <b-td
                                >{{
                                    showDate(
                                        lab.data.record_resource.ordered_date
                                    )
                                }}
                            </b-td>
                        </b-tr>
                        <b-tr
                            v-if="
                                lab.data.record_resource.antech_status &&
                                lab.data.record_resource.antech_status
                                    .antech_v6 &&
                                lab.data.record_resource.antech_status.status &&
                                lab.data.record_resource.antech_status.source ==
                                    'ANTECH'
                            "
                        >
                            <b-th>Status:</b-th>
                            <b-td>
                                <div
                                    v-if="
                                        lab.data.record_resource.antech_status
                                            .status == statuses.DRAFT_STATUS ||
                                        lab.data.record_resource.antech_status
                                            .status == statuses.PRE_ORDER_STATUS
                                    "
                                    class="antech-status-label"
                                >
                                    <span
                                        v-if="isWithinValidPeriod"
                                        class="text-danger"
                                    >
                                        You have until {{ validUntil }} to
                                        complete
                                    </span>
                                    <span v-else class="text-danger"
                                        >This order could not be completed. You
                                        may delete this record and create a new
                                        order.</span
                                    >
                                </div>
                                <b-badge
                                    :variant="
                                        antechStatus(
                                            lab.data.record_resource
                                                .antech_status.status
                                        )
                                    "
                                >
                                    {{
                                        lab.data.record_resource.antech_status
                                            .status
                                    }}
                                </b-badge></b-td
                            >
                        </b-tr>
                        <b-tr v-if="lab.data.record_resource.summary.status">
                            <b-th>Status:</b-th>
                            <b-td>
                                {{
                                    showStatusLabel(lab.data.record_resource)
                                }}</b-td
                            >
                        </b-tr>
                        <b-tr v-else>
                            <b-th>Received:</b-th>
                            <b-td>
                                {{
                                    showDate(
                                        lab.data.record_resource.received_date
                                    )
                                }}</b-td
                            >
                        </b-tr>
                        <b-tr>
                            <b-th>Next Due:</b-th>
                            <b-td>
                                {{ getNextDue(lab.data.record_resource) }}</b-td
                            >
                        </b-tr>
                        <b-tr v-if="getSendToLab(lab.data.record_resource)">
                            <b-th>{{ sendToLabText }}</b-th>
                            <b-td>
                                {{
                                    lab.data.record_resource.send_to == 1
                                        ? "Yes"
                                        : "No"
                                }}
                            </b-td>
                        </b-tr>
                        <b-tr v-if="requisitionId">
                            <b-th>Requisition Id:</b-th>
                            <b-td v-html="requisitionId"> </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Provider:</b-th>
                            <b-td>
                                {{
                                    lab.data.record_resource.prescribed_by
                                        ? lab.data.record_resource.prescribed_by
                                              .label
                                        : "None"
                                }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Invoice:</b-th>
                            <b-td
                                v-if="
                                    (lab.data.record_resource.selected_invoice &&
                                    lab.data.record_resource.selected_invoice
                                        .isactive)
                                "
                            >
                                <invoice-link
                                    :invoice="
                                        lab.data.record_resource
                                            .selected_invoice
                                    "
                                    :newTab="false"
                                />
                            </b-td>
                        </b-tr>
                        <b-tr
                            v-if="
                                lab.data.record_resource.isclinic &&
                                lab.data.lab_items.length == 1
                            "
                        >
                            <b-th>Print on Invoice:</b-th>
                            <b-td>
                                {{
                                    lab.data.record_resource.invoice_item
                                        ? lab.data.record_resource.invoice_item
                                              .print_on_invoice
                                            ? "Yes"
                                            : "No"
                                        : "Yes"
                                }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Medical Note:</b-th>
                            <b-td>
                                <a
                                    :href="getSoapURI(lab.data.record_resource)"
                                    >{{
                                        lab.data.soap_resources[
                                            lab.data.record_resource.soap_id
                                        ]
                                    }}</a
                                >
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Open Diagnoses:</b-th>
                            <b-td>
                                <b
                                    v-for="(diagnosis,
                                    index) in openDiagnosisOption"
                                    :key="diagnosis.id"
                                    @click="editDiagnosis(diagnosis)"
                                    class="text-primary cursor-pointer"
                                >
                                    {{ diagnosis.description }}
                                    <span
                                        v-if="
                                            index !=
                                            openDiagnosisOption.length - 1
                                        "
                                        >,</span
                                    >
                                </b>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Instruction:</b-th>
                            <b-td>
                                {{ lab.data.record_resource.instruction }}
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-th>Notes/Results:</b-th>
                            <b-td>
                                <html-content
                                    v-if="
                                        !_.isEmpty(
                                            lab.data.record_resource.record_note
                                        )
                                    "
                                    id="lab-display-html-content"
                                    :isReplaceNewLine="true"
                                    :content="
                                        lab.data.record_resource.record_note
                                    "
                                    :showSeeLess="false"
                                    :checkForEntities="true"
                                    :expanded="true"
                                />
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-col>
        </b-row>
        <modal-edit-diagnosis
            ref="modalEditDiagnosis"
            :patientId="patientId"
            @onsuccess="editSaved"
        />
    </div>
</template>
<script>
import PatientOwnerHover from "../patient/patient-owner-hover.vue";
import LabEdit from "./lab-edit";
import EXTENSIONS from "@/constants/extensions";
import Records from "@/services/records";
import LabHelper from "@/mixins/lab.js";
import { filter } from "lodash";
import HtmlContent from "@/components/commons/html-content";
import LAB from "@/constants/lab";

export default {
    components: {
        PatientPhoto: () => import("@/components/patient-photos/photo-display"),
        ClientName: () => import("@/components/commons/client-name"),
        ModalEditDiagnosis: () =>
            import("@/components/commons/modal-diagnosis"),
        InvoiceLink: () => import("@/components/commons/invoice-link"),
        PatientOwnerHover,
        LabEdit,
        HtmlContent
    },
    mixins: [LabHelper],
    props: {
        mode: {
            type: String
        },
        isSaving: {
            type: Boolean,
            default: false
        },
        patient: {
            type: Object
        },
        lab: {
            type: Object
        }
    },
    data() {
        return {
            CI_APPS: process.env.CI_APPS,
            showSendToLab: false,
            sendToLabText: "Send To Lab:",
            statuses: {
                DRAFT_STATUS: LAB.ANTECH_STATUS.DRAFT,
                PRE_ORDER_STATUS: LAB.ANTECH_STATUS.PRE_ORDER
            }
        };
    },
    watch: {},
    computed: {
        patientId() {
            return this.patient ? this.patient.id : "";
        },
        labSource() {
            return this.lab && this.lab.data.record_resource
                ? this.lab.data.record_resource.source
                : "";
        },
        isDataLoaded() {
            return this.patient ? true : false;
        },
        requisitionId() {
            let recordResource = this.lab.data.record_resource;
            let labSource = recordResource.source;
            if (!labSource) return false;
            if (labSource.includes("IDEXX_DEVICE")) labSource = "IDEXX_DEVICE";

            let requisitionId = null;

            switch (labSource) {
                case EXTENSIONS.CODE.CODE_ANTECH:
                case EXTENSIONS.CODE.CODE_ANTECH_EAST:
                case EXTENSIONS.CODE.CODE_ANTECH_WEST:
                case EXTENSIONS.CODE.CODE_IDEXX:
                case EXTENSIONS.CODE.CODE_IDEXX_DEVICE:
                case EXTENSIONS.CODE.CODE_MIDWEST:
                case EXTENSIONS.CODE.CODE_NBIOVET:
                case EXTENSIONS.CODE.CODE_ZNLABS:
                case EXTENSIONS.CODE.CODE_VETEK:
                case EXTENSIONS.CODE.CODE_MICROVET:
                case EXTENSIONS.CODE.CODE_BIONOTE:
                case EXTENSIONS.CODE.CODE_HESKA_CLOUD:
                    if (
                        recordResource.antechOrder &&
                        recordResource.antechOrder.requisition_id
                    ) {
                        requisitionId =
                            recordResource.antechOrder.requisition_id;

                        if (
                            (labSource === EXTENSIONS.CODE.CODE_IDEXX ||
                                labSource.includes("IDEXX_DEVICE")) &&
                            recordResource.antechOrder.result_received
                        ) {
                            requisitionId =
                                '<a href="' +
                                recordResource.antechOrder.result_url +
                                '" target="_blank">' +
                                recordResource.antechOrder.requisition_id +
                                "</a>";
                        }
                    }
                    break;
            }

            return requisitionId;
        },
        openDiagnosisOption() {
            if (this.lab.data.diagnoses_ids) {
                return filter(this.lab.data.diagnosis_resources, option => {
                    return this.lab.data.diagnoses_ids.includes(option.id);
                });
            }
            return [];
        },
        isWithinValidPeriod() {
            if (
                this.lab.data.record_resource.source &&
                this.lab.data.record_resource.antech_status &&
                this.lab.data.record_resource.antech_status.order_date
            ) {
                let orderDate = this._toDateTime(
                    this.lab.data.record_resource.antech_status.order_date?.date
                );

                let duration = this._now().diff(orderDate, ["days"]).toObject();
                let period = LAB.ANTECH_VALIDITY_PERIOD + 1;

                return duration && duration.days < period;
            }

            return false;
        },
        validUntil() {
            if (
                this.lab.data.record_resource.source &&
                this.lab.data.record_resource.antech_status
            ) {
                return this._toDateTime(
                    this.lab.data.record_resource.antech_status?.order_date
                        ?.date
                )
                    .plus({ days: 29 })
                    .toFormat(this._dateFormat);
            }

            return "";
        }
    },
    beforeDestroy() {},
    methods: {
        getEntityBreed(patient) {
            var breeds = [];

            if (
                patient.patient_breeds &&
                _.keys(patient.patient_breeds).length > 0
            ) {
                $.each(patient.patient_breeds, function (i, v) {
                    breeds.push(v.name + " (" + v.species_name + ")");
                });
            }

            return breeds.join(", ");
        },
        getStatus(patient) {
            let status = "Active";
            if (patient.isactive === false) {
                status = "Deleted";
            } else if (patient.status === 2) {
                status = "Inactive";
            } else if (patient.status === 3) {
                status = "Deceased";
            }

            if (patient.status > 1) {
                status +=
                    " (" + this._carbonDateDisplay(patient.status_date) + ")";
            }

            return status;
        },
        getFullName(details) {
            return details.lastname + ", " + details.firstname;
        },
        getSex() {
            if (this.patient.patient_sex) {
                return this.patient.patient_sex.value;
            }
            return "";
        },
        getDateInSeconds(date) {
            return date ? this._fromDateCarbon(date).toSeconds() : null;
        },
        getSoapURI(recordData) {
            return (
                process.env.CI_APPS +
                "soap/soap/view/" +
                recordData.soap_id_encoded
            );
        },
        getLabSource(data) {
            let displayDevice = "";
            if (data) {
                let source = data.record_resource.source
                    ? data.record_resource.source
                    : EXTENSIONS.CODE.CODE_OTHER;
                let resourceOption = data.source_resources;
                displayDevice = resourceOption[source];
                // show * for old heska source
                if (
                    source == EXTENSIONS.CODE.CODE_ONPREM_HESKA ||
                    source == EXTENSIONS.CODE.CODE_ONPREM_HESKA_UPPERCASE
                ) {
                    displayDevice = EXTENSIONS.CODE.CODE_ONPREM_HESKA + "*";
                } else if (source == EXTENSIONS.CODE.CODE_HESKA_CLOUD) {
                    displayDevice = EXTENSIONS.CODE.CODE_ONPREM_HESKA;
                } else if (source == EXTENSIONS.CODE.CODE_ONPREM_ABAXIS) {
                    displayDevice = EXTENSIONS.CODE.CODE_ONPREM_ABAXIS;
                }
            }
            return displayDevice;
        },
        getLabStatus() {},
        getNextDue(recordData) {
            if (recordData.reminder_date && recordData.reminder_date != "") {
                if (typeof recordData.reminder_date == "string")
                    return recordData.reminder_date;
                return this._carbonDateTimeDisplay(recordData.reminder_date);
            } else if (recordData.item_expire && recordData.item_expire != "") {
                if (typeof recordData.item_expire == "string")
                    return recordData.item_expire;
                return this._carbonDateTimeDisplay(recordData.item_expire);
            }

            return "";
        },
        getSendToLab(recordData) {
            let self = this;
            let labSource = recordData.source;
            if (!labSource) return false;
            if (labSource.includes("IDEXX_DEVICE")) labSource = "IDEXX_DEVICE";

            switch (labSource) {
                case EXTENSIONS.CODE.CODE_ANTECH:
                case EXTENSIONS.CODE.CODE_ANTECH_EAST:
                case EXTENSIONS.CODE.CODE_ANTECH_WEST:
                case EXTENSIONS.CODE.CODE_IDEXX:
                case EXTENSIONS.CODE.CODE_IDEXX_DEVICE:
                case EXTENSIONS.CODE.CODE_MIDWEST:
                case EXTENSIONS.CODE.CODE_NBIOVET:
                case EXTENSIONS.CODE.CODE_ZNLABS:
                case EXTENSIONS.CODE.CODE_VETEK:
                case EXTENSIONS.CODE.CODE_MICROVET:
                case EXTENSIONS.CODE.CODE_BIONOTE:
                case EXTENSIONS.CODE.CODE_HESKA_CLOUD:
                    if (recordData.send_to == 1 || recordData.send_to == "1") {
                        self.showSendToLab = true;
                    } else {
                        if (
                            labSource == EXTENSIONS.CODE.CODE_ANTECH_EAST ||
                            labSource == EXTENSIONS.CODE.CODE_ANTECH_WEST
                        ) {
                            if (
                                self._isExtensionEnabled(
                                    EXTENSIONS.CODE.CODE_ANTECH
                                )
                            ) {
                                self.showSendToLab = true;
                            }
                        } else {
                            if (labSource.includes("IDEXX_DEVICE")) {
                                labSource = EXTENSIONS.CODE.CODE_IDEXX;
                            }

                            if (self._isExtensionEnabled(labSource)) {
                                self.showSendToLab = true;
                            }
                        }
                    }

                    break;

                default:
                    if (
                        recordData.lab_type ==
                            EXTENSIONS.CODE.CODE_ABAXIS_API &&
                        (self._isExtensionEnabled(
                            EXTENSIONS.CODE.CODE_ABAXIS_API
                        ) ||
                            self._isExtensionEnabled(
                                EXTENSIONS.CODE.CODE_ABAXIS_FUSE
                            ))
                    ) {
                        self.showSendToLab = true;
                    }
                    break;
            }

            if (self.showSendToLab) {
                if (recordData.lab_type == EXTENSIONS.CODE.CODE_ABAXIS_API) {
                    self.sendToLabText = "Send To Analyzer:";
                } else if (labSource == EXTENSIONS.CODE.CODE_HESKA_CLOUD) {
                    self.sendToLabText = "Send To Analyzer:";
                } else if (recordData.is_idexx_in_house) {
                    self.sendToLabText = "Send To VetLab:";
                }
            }

            return self.showSendToLab;
        },
        editDiagnosis(diagnosis) {
            let self = this;
            Records.getDiagnosisDetails(
                diagnosis.patient_id,
                diagnosis.id
            ).then(response => {
                if (response.message.code === 200) {
                    self.$refs.modalEditDiagnosis.open({
                        action: "edit",
                        editRecord: response.resource
                    });
                }
            });
        },
        editSaved(details) {
            this.$emit("edit-saved", details);
        },
        saveEdit() {
            this.$refs.labEdit.save();
        },
        showDate(date) {
            if (!date) return null;
            return this._carbonDateTimeDisplay(date);
        },
        antechStatus(orderStatus) {
            let badgeStatus;
            switch (orderStatus) {
                case LAB.ANTECH_STATUS.SUBMITTED:
                case LAB.ANTECH_STATUS.FINAL:
                case LAB.ANTECH_STATUS.UPDATED:
                    badgeStatus = "success";
                    break;
                case LAB.ANTECH_STATUS.IN_PROGRESS:
                case LAB.ANTECH_STATUS.PARTIAL:
                    badgeStatus = "light";
                    break;
                case LAB.ANTECH_STATUS.INC_PRE_ORDER:
                case LAB.ANTECH_STATUS.DRAFT:
                    badgeStatus = "dark";
                    break;
                case LAB.ANTECH_STATUS.PRE_ORDER:
                    badgeStatus = "info";
                    break;
                default:
                    badgeStatus = "secondary";
                    break;
            }

            return badgeStatus;
        }
    }
};
</script>
<style type="text/css" lang="scss" scoped>
// .profile-details table tbody tr td:first-child {
//     font-weight: bold;
// }

.profile-details .patient .table th,
.profile-details .patient .table td {
    padding: 0px;
    line-height: 30px;
}

.antech-status-label {
    line-height: normal;
}

#lab-display-html-content {
    max-width: 400px;
    padding-top: 2px;
}
</style>
